import React, { useState, Component } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, CreditCardIcon, LandmarkIcon, SmartphoneIcon, PenLineIcon, LinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { THAI_PROVINCES, THAI_BANKS, mockSuppliers } from '../utils/mockSuppliers';
export function SupplierForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get('id');
  const isEditing = !!supplierId;
  const editingSupplier = isEditing ? mockSuppliers.find(s => s.id === supplierId) || null : null;
  const [entryMethod, setEntryMethod] = useState<'manual' | 'invite'>('manual');
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>(editingSupplier?.supplierType || 'company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [formData, setFormData] = useState({
    // Shared
    email: editingSupplier?.email || '',
    phone: editingSupplier?.phone || '',
    useAsBillingEmail: editingSupplier?.useAsBillingEmail ?? true,
    addressLine1: editingSupplier?.addressLine1 || '',
    addressLine2: editingSupplier?.addressLine2 || '',
    city: editingSupplier?.city || '',
    province: editingSupplier?.province || '',
    country: 'Thailand',
    postcode: editingSupplier?.postcode || '',
    internalReference: editingSupplier?.internalReference || '',
    // Company
    taxId: editingSupplier?.taxId || '',
    companyNameTh: editingSupplier?.companyNameTh || '',
    companyNameEn: editingSupplier?.companyNameEn || '',
    branch: editingSupplier?.branch || '00000',
    vatRegistered: editingSupplier?.vatRegistered ?? false,
    contactPerson: editingSupplier?.contactPerson || '',
    // Individual
    idType: editingSupplier?.idType || 'citizen_id',
    idNumber: editingSupplier?.idNumber || '',
    fullNameTh: editingSupplier?.fullNameTh || '',
    fullNameEn: editingSupplier?.fullNameEn || '',
    individualTaxId: editingSupplier?.individualTaxId || '',
    // Pay-in method
    payInMethod: editingSupplier?.payInMethod || 'acquiring',
    cardholderName: editingSupplier?.cardholderName || '',
    cardNumber: editingSupplier?.cardNumber || '',
    expiryDate: editingSupplier?.expiryDate || '',
    // Payout method
    payoutMethod: editingSupplier?.payoutMethod || 'bank_account',
    bankName: editingSupplier?.bankName || '',
    accountNumber: editingSupplier?.accountNumber || '',
    accountName: editingSupplier?.accountName || '',
    promptPayType: editingSupplier?.promptPayType || 'phone',
    promptPayId: editingSupplier?.promptPayId || '',
    promptPayName: editingSupplier?.promptPayName || ''
  });
  // DBD lookup state for API response metadata
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [copied, setCopied] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [linkGenerated, setLinkGenerated] = useState(false);
  const inviteLink = `${window.location.origin}/supplier-onboarding/abc123def`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        country: 'Thailand',
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: 'Test Company Limited',
        companyNameEn: 'Test Company Limited',
        addressLine1: '123/45 Sukhumvit Road',
        addressLine2: 'Khlong Toei, Khlong Toei',
        city: 'Bangkok',
        country: 'Thailand',
        postcode: '10110'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/suppliers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-11356">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-11357">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-11358">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-11359" />
          Back to Suppliers
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11360">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-11361">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-11362">
            {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-11363">
          {/* SECTION 1: Supplier Information */}
          <div className="space-y-6" data-id="element-11364">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-11365">
              Supplier Information
            </h2>

            {/* Entry Method Selection */}
            {!isEditing && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11366">
                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'manual' ? 'border-[#F7941D] ring-1 ring-[#F7941D] bg-orange-50/30' : 'border-gray-200'}`} data-id="element-11367">
                  <input type="radio" name="entryMethod" value="manual" checked={entryMethod === 'manual'} onChange={() => setEntryMethod('manual')} className="sr-only" data-id="element-11368" />
                  <div className="flex w-full items-start" data-id="element-11369">
                    <div className="flex items-center h-5" data-id="element-11370">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'manual' ? 'border-[#F7941D]' : 'border-gray-300'}`} data-id="element-11371">
                        {entryMethod === 'manual' && <div className="w-2 h-2 rounded-full bg-[#F7941D]" data-id="element-11372" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-11373">
                      <div className="flex items-center gap-2 mb-1" data-id="element-11374">
                        <PenLineIcon size={16} className={entryMethod === 'manual' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-11375" />
                        <span className={`text-sm font-medium ${entryMethod === 'manual' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-11376">
                          Enter details myself
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-11377">
                        Fill in supplier identity, contact, and payout details
                        on their behalf
                      </p>
                    </div>
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'invite' ? 'border-[#F7941D] ring-1 ring-[#F7941D] bg-orange-50/30' : 'border-gray-200'}`} data-id="element-11378">
                  <input type="radio" name="entryMethod" value="invite" checked={entryMethod === 'invite'} onChange={() => setEntryMethod('invite')} className="sr-only" data-id="element-11379" />
                  <div className="flex w-full items-start" data-id="element-11380">
                    <div className="flex items-center h-5" data-id="element-11381">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'invite' ? 'border-[#F7941D]' : 'border-gray-300'}`} data-id="element-11382">
                        {entryMethod === 'invite' && <div className="w-2 h-2 rounded-full bg-[#F7941D]" data-id="element-11383" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-11384">
                      <div className="flex items-center gap-2 mb-1" data-id="element-11385">
                        <LinkIcon size={16} className={entryMethod === 'invite' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-11386" />
                        <span className={`text-sm font-medium ${entryMethod === 'invite' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-11387">
                          Invite supplier
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-11388">
                        Generate a secure link for the supplier to input their
                        own details
                      </p>
                    </div>
                  </div>
                </label>
              </div>}

            {/* Supplier Type Toggle (Always visible) */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-11389">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-11390">
                <BuildingIcon size={16} className="mr-2" data-id="element-11391" />
                Company
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-11392">
                <UserIcon size={16} className="mr-2" data-id="element-11393" />
                Individual
              </button>
            </div>

            {/* MANUAL ENTRY FLOW */}
            {entryMethod === 'manual' && <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-11394">
                {/* --- COMPANY FIELDS --- */}
                {supplierType === 'company' && <div className="space-y-6" data-id="element-11395">
                    {/* DBD Lookup Section */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-11396">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11397">
                        Tax ID / Registration Number *
                      </label>
                      <div className="flex gap-3" data-id="element-11398">
                        <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder="13-digit registration number" className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-11399" />
                        <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] disabled:bg-[#F7941D]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11400">
                          {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-11401" /> : <>
                              <SearchIcon size={18} className="mr-2" data-id="element-11402" />
                              Lookup
                            </>}
                        </button>
                      </div>

                      {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-11403">
                          {lookupError}
                        </p>}

                      {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-11404">
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start" data-id="element-11405">
                            <CheckCircle2Icon size={20} className="text-green-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-11406" />
                            <div data-id="element-11407">
                              <p className="text-sm font-medium text-green-800" data-id="element-11408">
                                Company details fetched from Department of
                                Business Development (DBD)
                              </p>
                              <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-green-700" data-id="element-11409">
                                {dbdMeta.registerDate && <>
                                    <span data-id="element-11410">
                                      Registered: {dbdMeta.registerDate}
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-11411"></span>
                                  </>}
                                {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-11412">
                                    {dbdMeta.juristicStatus}
                                  </span>}
                                {dbdMeta.juristicType && <>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-11413"></span>
                                    <span data-id="element-11414">{dbdMeta.juristicType}</span>
                                  </>}
                              </div>
                            </div>
                          </div>
                          {dbdMeta.isMockFallback && <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-11415">
                              <p className="text-xs text-amber-700" data-id="element-11416">
                                <span className="font-medium" data-id="element-11417">Note:</span>{' '}
                                Direct browser request to DBD API was blocked by
                                CORS. Showing mock data. In production, route
                                API calls through your backend proxy.
                              </p>
                            </div>}
                        </div>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11418">
                      <div data-id="element-11419">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11420">
                          Company name (Local) *
                        </label>
                        <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess ? 'bg-orange-50' : ''}`} data-id="element-11421" />
                      </div>
                      <div data-id="element-11422">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11423">
                          Company name (English)
                        </label>
                        <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess ? 'bg-orange-50' : ''}`} data-id="element-11424" />
                      </div>
                      <div data-id="element-11425">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11426">
                          Branch number *
                        </label>
                        <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11427" />
                      </div>
                      <div data-id="element-11428">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11429">
                          Contact person name
                        </label>
                        <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11430" />
                      </div>
                    </div>

                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-11431">
                      <div className="relative flex items-center justify-center" data-id="element-11432">
                        <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-11433" />
                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#F7941D] peer-checked:border-[#F7941D] transition-colors" data-id="element-11434"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-11435">
                          <polyline points="20 6 9 17 4 12" data-id="element-11436"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-11437">
                        VAT registered company
                      </span>
                    </label>
                  </div>}

                {/* --- INDIVIDUAL FIELDS --- */}
                {supplierType === 'individual' && <div className="space-y-6" data-id="element-11438">
                    <div className="flex gap-6 mb-2" data-id="element-11439">
                      <label className="flex items-center cursor-pointer" data-id="element-11440">
                        <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11441" />
                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-11442">
                          Thai Citizen ID
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer" data-id="element-11443">
                        <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11444" />
                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-11445">
                          Passport
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11446">
                      <div className="md:col-span-2" data-id="element-11447">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11448">
                          {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                        </label>
                        <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11449" />
                      </div>
                      <div data-id="element-11450">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11451">
                          Full name (Local)
                        </label>
                        <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11452" />
                      </div>
                      <div data-id="element-11453">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11454">
                          Full name *
                        </label>
                        <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11455" />
                      </div>
                      <div className="md:col-span-2" data-id="element-11456">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11457">
                          Tax ID (if different from Citizen ID)
                        </label>
                        <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11458" />
                      </div>
                    </div>
                  </div>}

                {/* --- SHARED CONTACT FIELDS --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-11459">
                  <div data-id="element-11460">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11461">
                      Email address *
                    </label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="Enter email address" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11462" />
                  </div>
                  <div data-id="element-11463">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11464">
                      Phone number
                    </label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="e.g. 0812345678" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11465" />
                  </div>
                  <div className="md:col-span-2" data-id="element-11466">
                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-11467">
                      <div className="relative flex items-center justify-center" data-id="element-11468">
                        <input type="checkbox" name="useAsBillingEmail" checked={formData.useAsBillingEmail} onChange={handleChange} className="peer sr-only" data-id="element-11469" />
                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#F7941D] peer-checked:border-[#F7941D] transition-colors" data-id="element-11470"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-11471">
                          <polyline points="20 6 9 17 4 12" data-id="element-11472"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-11473">
                        Use this email as the billing email
                      </span>
                    </label>
                  </div>
                </div>

                {/* Divider: Billing information */}
                <div className="relative py-2" data-id="element-11474">
                  <div className="absolute inset-0 flex items-center" data-id="element-11475">
                    <div className="w-full border-t border-gray-200" data-id="element-11476"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-11477">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11478">
                      Billing information
                    </span>
                  </div>
                </div>

                {/* Billing Info Fields */}
                <div className="space-y-5" data-id="element-11479">
                  <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder="Address line 1" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11480" />
                  <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder="Address line 2" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11481" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11482">
                    <div data-id="element-11483">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-11484">
                        City / District
                      </label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Khlong Toei" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11485" />
                    </div>
                    <div data-id="element-11486">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-11487">
                        Province
                      </label>
                      <SearchableSelect name="province" options={THAI_PROVINCES} value={formData.province} onChange={val => setFormData(prev => ({
                    ...prev,
                    province: val
                  }))} placeholder="Select province" data-id="element-11488" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11489">
                    <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-11490">
                      Thailand
                    </div>
                    <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder="Postcode (5 digits)" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11491" />
                  </div>
                </div>

                {/* Divider: Additional information */}
                <div className="relative py-2" data-id="element-11492">
                  <div className="absolute inset-0 flex items-center" data-id="element-11493">
                    <div className="w-full border-t border-gray-200" data-id="element-11494"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-11495">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11496">
                      Additional information
                    </span>
                  </div>
                </div>

                {/* Additional Info Fields */}
                <div data-id="element-11497">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11498">
                    Internal reference
                  </label>
                  <input type="text" name="internalReference" value={formData.internalReference} onChange={handleChange} placeholder="Enter internal reference" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11499" />
                </div>

                {/* Divider: Payout method */}
                <div className="relative py-2" data-id="element-11500">
                  <div className="absolute inset-0 flex items-center" data-id="element-11501">
                    <div className="w-full border-t border-gray-200" data-id="element-11502"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-11503">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11504">
                      Payout method
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 -mt-4" data-id="element-11505">
                  Choose how this supplier will receive their funds.
                </p>

                {/* Payout Method Selection */}
                <div className="space-y-4" data-id="element-11506">
                  {/* Bank Account Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11507">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11508">
                      <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11509" />
                      <div className="ml-3 flex items-center" data-id="element-11510">
                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-11511">
                          <LandmarkIcon size={16} className="text-green-600" data-id="element-11512" />
                        </div>
                        <div data-id="element-11513">
                          <div className="text-sm font-medium text-gray-900" data-id="element-11514">
                            Bank Account
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-11515">
                            Direct transfer to a Thai bank account
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-11516">
                        <div data-id="element-11517">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11518">
                            Bank name
                          </label>
                          <SearchableSelect name="bankName" options={THAI_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                      ...prev,
                      bankName: val
                    }))} placeholder="Select a bank" data-id="element-11519" />
                        </div>
                        <div data-id="element-11520">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11521">
                            Account number
                          </label>
                          <input type="text" name="accountNumber" maxLength={12} placeholder="10–12 digits" value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11522" />
                        </div>
                        <div data-id="element-11523">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11524">
                            Account name
                          </label>
                          <input type="text" name="accountName" placeholder="Name on the bank account" value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11525" />
                        </div>
                      </div>}
                  </div>

                  {/* PromptPay Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11526">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11527">
                      <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11528" />
                      <div className="ml-3 flex items-center" data-id="element-11529">
                        <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-11530">
                          <SmartphoneIcon size={16} className="text-purple-600" data-id="element-11531" />
                        </div>
                        <div data-id="element-11532">
                          <div className="text-sm font-medium text-gray-900" data-id="element-11533">
                            PromptPay
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-11534">
                            Fast transfer via Phone, Citizen ID, or Tax ID
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-11535">
                        <div data-id="element-11536">
                          <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-11537">
                            PromptPay Type
                          </label>
                          <div className="flex flex-wrap gap-4" data-id="element-11538">
                            <label className="flex items-center" data-id="element-11539">
                              <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11540" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-11541">
                                Phone Number
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-11542">
                              <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11543" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-11544">
                                Citizen ID
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-11545">
                              <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11546" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-11547">
                                Tax ID
                              </span>
                            </label>
                          </div>
                        </div>
                        <div data-id="element-11548">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11549">
                            PromptPay ID
                          </label>
                          <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11550" />
                        </div>
                        <div data-id="element-11551">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11552">
                            Display name (for verification)
                          </label>
                          <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11553" />
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}

            {/* INVITE SUPPLIER FLOW */}
            {entryMethod === 'invite' && <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-11554">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6" data-id="element-11555">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-11556">
                    Invite Supplier
                  </h3>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-11557">
                    Enter a name for this supplier, then generate a secure link
                    they can use to provide their identity details, contact
                    information, and payout method.
                  </p>

                  <div className="space-y-4" data-id="element-11558">
                    <div data-id="element-11559">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-11560">
                        Supplier Name *
                      </label>
                      <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="e.g. Siam Fresh Market" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-11561" />
                    </div>

                    {!linkGenerated && <button type="button" onClick={() => {
                  if (inviteName.trim()) setLinkGenerated(true);
                }} disabled={!inviteName.trim()} className="flex items-center px-5 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11562">
                        <LinkIcon size={16} className="mr-2" data-id="element-11563" />
                        Generate Invite Link
                      </button>}

                    {linkGenerated && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-11564">
                        <div data-id="element-11565">
                          <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-11566">
                            Secure Link
                          </label>
                          <div className="flex gap-2" data-id="element-11567">
                            <input type="text" readOnly value={inviteLink} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-600 font-mono" data-id="element-11568" />
                            <button type="button" onClick={handleCopyLink} className="flex items-center justify-center px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors shadow-sm min-w-[100px]" data-id="element-11569">
                              {copied ? <>
                                  <CheckIcon size={16} className="mr-2 text-green-600" data-id="element-11570" />{' '}
                                  Copied
                                </> : <>
                                  <CopyIcon size={16} className="mr-2" data-id="element-11571" /> Copy
                                </>}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-11572">
                          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" data-id="element-11573"></div>
                          <span className="text-sm font-medium text-amber-800" data-id="element-11574">
                            Pending — Waiting for supplier to complete
                          </span>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}
          </div>

          {/* SECTION 2: Funds Source (Pay-in) - ALWAYS VISIBLE */}
          <div className="space-y-6 pt-8 border-t border-gray-200" data-id="element-11575">
            <div data-id="element-11576">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-11577">
                Funds Source (Pay-in)
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-11578">
                Choose how you will fund payments to this supplier. Only you can
                configure this.
              </p>
            </div>

            {/* Pay-in Method Selection */}
            <div className="space-y-4" data-id="element-11579">
              {/* Acquiring Volume Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'acquiring' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11580">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11581">
                  <input type="radio" name="payInMethod" value="acquiring" checked={formData.payInMethod === 'acquiring'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11582" />
                  <div className="ml-3 flex items-center" data-id="element-11583">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-11584">
                      <LandmarkIcon size={16} className="text-green-600" data-id="element-11585" />
                    </div>
                    <div data-id="element-11586">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11587">
                        Acquiring Volume
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11588">
                        Use your merchant acquiring balance — funds collected
                        from customer card payments are used to pay this
                        supplier
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'acquiring' && <div className="p-4 bg-gray-50 border-t border-gray-100 rounded-b-xl" data-id="element-11589">
                    <p className="text-xs text-gray-600" data-id="element-11590">
                      Supplier payments will be deducted from your acquiring
                      settlement balance before payout. No additional funding
                      source is needed — your incoming card transaction volume
                      covers it.
                    </p>
                  </div>}
              </div>

              {/* Card Payment Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'card' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11591">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11592">
                  <input type="radio" name="payInMethod" value="card" checked={formData.payInMethod === 'card'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11593" />
                  <div className="ml-3 flex items-center" data-id="element-11594">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-3" data-id="element-11595">
                      <CreditCardIcon size={16} className="text-blue-600" data-id="element-11596" />
                    </div>
                    <div data-id="element-11597">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11598">
                        Card Payment
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11599">
                        Link your own debit or credit card to fund supplier
                        payments
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'card' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-11600">
                    <p className="text-xs text-gray-500" data-id="element-11601">
                      Your card will be charged each time a payment is made to
                      this supplier. Card details are stored securely.
                    </p>
                    <div data-id="element-11602">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11603">
                        Name on card
                      </label>
                      <input type="text" name="cardholderName" placeholder="Your name as it appears on the card" value={formData.cardholderName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11604" />
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11605">
                      <div className="col-span-2" data-id="element-11606">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11607">
                          Card number
                        </label>
                        <input type="text" name="cardNumber" placeholder="XXXX XXXX XXXX XXXX" value={formData.cardNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11608" />
                      </div>
                      <div data-id="element-11609">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11610">
                          Expiry date
                        </label>
                        <input type="text" name="expiryDate" placeholder="MM/YY" value={formData.expiryDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11611" />
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100" data-id="element-11612">
            <button type="button" onClick={() => navigate('/suppliers')} className="px-6 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-11613">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11614">
              Save Supplier
            </button>
          </div>
        </form>
      </div>
    </div>;
}